export const ANNOTATION_FORMAT_VERSION = 2;

const BRUSH_TOOL = "Brush";
const PASS_THROUGH_OP_KEYS = new Set(["lisonsAreasData"]);
const EXCLUDED_OP_TOOLS = new Set(["MultiPointProbe", "MultiLine", "LisonProbe"]);

function roundCoord(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return null;
  }
  return Math.round(Number(value));
}

export function pointToTuple(point) {
  if (!point || point.x == null || point.y == null) {
    return null;
  }
  return [roundCoord(point.x), roundCoord(point.y)];
}

export function tupleToPoint(tuple) {
  if (!Array.isArray(tuple) || tuple.length < 2) {
    return null;
  }
  return { x: tuple[0], y: tuple[1] };
}

export function pointsToTuples(points) {
  if (!Array.isArray(points)) {
    return [];
  }
  return points
    .map((point) => pointToTuple(point))
    .filter(Boolean);
}

export function tuplesToHandlePoints(tuples) {
  if (!Array.isArray(tuples)) {
    return [];
  }
  return tuples.map((tuple) => tupleToPoint(tuple)).filter(Boolean);
}

export function normalizeFrameKey(frameKey) {
  if (frameKey == null) {
    return frameKey;
  }
  const key = String(frameKey);
  const frameMatch = key.match(/(?:^|\?)frame=(\d+)$/);
  if (frameMatch) {
    return `frame=${frameMatch[1]}`;
  }
  if (/^frame=\d+$/.test(key)) {
    return key;
  }
  return key;
}

function toolDataToArray(toolState) {
  if (!toolState) {
    return [];
  }
  if (Array.isArray(toolState)) {
    return toolState;
  }
  if (Array.isArray(toolState.data)) {
    return toolState.data;
  }
  if (toolState.data && typeof toolState.data === "object") {
    return Object.keys(toolState.data)
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => toolState.data[key]);
  }
  return [];
}

/** Normalize OP/OPT tool state data to a flat item array (array or object data). */
export function getToolStateDataItems(toolState) {
  return toolDataToArray(toolState);
}

function isCompactToolValue(value) {
  return Array.isArray(value);
}

function isLegacyToolValue(value) {
  return (
    value != null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    ("data" in value || "handles" in value)
  );
}

export function isCompactAnnotationFormat(payload) {
  if (!payload || typeof payload !== "object") {
    return false;
  }
  if (payload.annotationFormatVersion === ANNOTATION_FORMAT_VERSION) {
    return true;
  }

  const op = payload.OP;
  if (op?.Etdrs && isCompactToolValue(op.Etdrs)) {
    return true;
  }

  const opt = payload.OPT;
  if (opt && typeof opt === "object") {
    for (const frameKey of Object.keys(opt)) {
      const frame = opt[frameKey];
      if (!frame || typeof frame !== "object") {
        continue;
      }
      for (const toolName of Object.keys(frame)) {
        if (isCompactToolValue(frame[toolName])) {
          return true;
        }
      }
    }
  }

  return false;
}

function serializeLineItem(item) {
  if (!item) {
    return null;
  }
  const points = pointsToTuples(item.handles?.points || item.points);
  if (!item.name && points.length === 0) {
    return null;
  }
  const compact = {
    name: item.name,
    visible: item.visible !== false,
    points,
  };
  if (item.color != null) {
    compact.color = item.color;
  }
  if (item.label != null) {
    compact.label = item.label;
  }
  return compact;
}

function hydrateLineItem(item) {
  if (!item) {
    return null;
  }
  return {
    name: item.name,
    visible: item.visible !== false,
    handles: {
      points: tuplesToHandlePoints(item.points || []),
    },
    invalidated: true,
  };
}

function serializeAiSegmentationItem(item) {
  if (!item) {
    return null;
  }
  const points = pointsToTuples(item.points);
  if (!item.label && points.length === 0) {
    return null;
  }
  return {
    label: item.label,
    color: item.color,
    active: item.active !== false,
    visible: item.visible !== false,
    points,
  };
}

function hydrateAiSegmentationItem(item) {
  if (!item) {
    return null;
  }
  return {
    label: item.label,
    color: item.color,
    active: item.active !== false,
    visible: item.visible !== false,
    points: tuplesToHandlePoints(item.points || []),
    invalidated: true,
  };
}

function serializeFluidSegmentationItem(item) {
  if (!item) {
    return null;
  }
  const compact = {
    label: item.label,
    color: item.color,
    visible: item.visible !== false,
  };
  if (item.alpha != null) {
    compact.alpha = item.alpha;
  }
  if (item.start && item.end) {
    compact.start = pointToTuple(item.start);
    compact.end = pointToTuple(item.end);
  } else if (Array.isArray(item.points) && item.points.length > 0) {
    compact.points = pointsToTuples(item.points);
    if (item.preserveStrokeOrder != null) {
      compact.preserveStrokeOrder = item.preserveStrokeOrder;
    }
  } else if (item.handles?.start && item.handles?.end) {
    compact.start = pointToTuple(item.handles.start);
    compact.end = pointToTuple(item.handles.end);
  } else {
    return null;
  }
  return compact;
}

function hydrateFluidSegmentationItem(item) {
  if (!item) {
    return null;
  }
  const hydrated = {
    label: item.label,
    color: item.color,
    visible: item.visible !== false,
    invalidated: true,
  };
  if (item.alpha != null) {
    hydrated.alpha = item.alpha;
  }
  if (item.start && item.end) {
    hydrated.start = tupleToPoint(item.start);
    hydrated.end = tupleToPoint(item.end);
  } else if (Array.isArray(item.points) && item.points.length > 0) {
    hydrated.points = tuplesToHandlePoints(item.points);
    if (item.preserveStrokeOrder != null) {
      hydrated.preserveStrokeOrder = item.preserveStrokeOrder;
    }
  }
  return hydrated;
}

function serializeLisonItem(item) {
  if (!item) {
    return null;
  }
  const start = pointToTuple(item.handles?.start);
  const end = pointToTuple(item.handles?.end);
  if (!start || !end) {
    return null;
  }
  const compact = {
    visible: item.visible !== false,
    start,
    end,
  };
  if (item.length != null) {
    compact.length = item.length;
  }
  return compact;
}

function hydrateLisonItem(item) {
  if (!item) {
    return null;
  }
  const start = tupleToPoint(item.start);
  const end = tupleToPoint(item.end);
  if (!start || !end) {
    return null;
  }
  return {
    visible: item.visible !== false,
    length: item.length,
    handles: { start, end },
    invalidated: true,
  };
}

function serializeEtdrsItem(item) {
  if (!item) {
    return null;
  }
  const center = pointToTuple(item.handles?.start || item.center);
  if (!center) {
    return null;
  }
  const compact = {
    center,
    visible: item.visible !== false,
  };
  if (Array.isArray(item.circles_radii)) {
    compact.circles_radii = item.circles_radii;
  }
  if (item.volumesData) {
    compact.volumesData = item.volumesData;
  }
  return compact;
}

function hydrateEtdrsItem(item) {
  if (!item) {
    return null;
  }
  const center = tupleToPoint(item.center);
  if (!center) {
    return null;
  }
  return {
    visible: item.visible !== false,
    circles_radii: item.circles_radii,
    volumesData: item.volumesData,
    handles: {
      start: center,
      end: center,
    },
    invalidated: true,
  };
}

function serializeHandlePairItem(item) {
  if (!item) {
    return null;
  }
  const compact = {
    visible: item.visible !== false,
  };
  const start = pointToTuple(item.handles?.start);
  const end = pointToTuple(item.handles?.end);
  if (start) {
    compact.start = start;
  }
  if (end) {
    compact.end = end;
  }
  if (Array.isArray(item.handles?.points)) {
    compact.points = pointsToTuples(item.handles.points);
  }
  if (item.text != null) {
    compact.text = item.text;
  }
  if (item.length != null) {
    compact.length = item.length;
  }
  if (item.name != null) {
    compact.name = item.name;
  }
  if (item.color != null) {
    compact.color = item.color;
  }
  if (item.label != null) {
    compact.label = item.label;
  }
  return compact;
}

function hydrateHandlePairItem(item) {
  if (!item) {
    return null;
  }
  const hydrated = {
    visible: item.visible !== false,
    invalidated: true,
    handles: {},
  };
  if (item.start) {
    hydrated.handles.start = tupleToPoint(item.start);
  }
  if (item.end) {
    hydrated.handles.end = tupleToPoint(item.end);
  }
  if (Array.isArray(item.points) && item.points.length > 0) {
    hydrated.handles.points = tuplesToHandlePoints(item.points);
  }
  if (item.text != null) {
    hydrated.text = item.text;
  }
  if (item.length != null) {
    hydrated.length = item.length;
  }
  if (item.name != null) {
    hydrated.name = item.name;
  }
  if (item.color != null) {
    hydrated.color = item.color;
  }
  if (item.label != null) {
    hydrated.label = item.label;
  }
  return hydrated;
}

function serializeOptTool(toolName, toolState) {
  if (toolName === BRUSH_TOOL) {
    return toolState;
  }

  const items = toolDataToArray(toolState);
  if (!items.length) {
    return null;
  }

  let serializedItems = [];
  switch (toolName) {
    case "Line":
      serializedItems = items
        .map(serializeLineItem)
        .filter(Boolean)
        .filter((item) => item.name !== "Segment");
      break;
    case "AiSegmentation":
      serializedItems = items.map(serializeAiSegmentationItem).filter(Boolean);
      break;
    case "FluidSegmentation":
      serializedItems = items
        .map(serializeFluidSegmentationItem)
        .filter(Boolean);
      break;
    case "Lison":
      serializedItems = items.map(serializeLisonItem).filter(Boolean);
      break;
    default:
      serializedItems = items.map(serializeHandlePairItem).filter(Boolean);
      break;
  }

  return serializedItems.length ? serializedItems : null;
}

function hydrateOptTool(toolName, toolValue) {
  if (toolName === BRUSH_TOOL || isLegacyToolValue(toolValue)) {
    return toolValue;
  }
  if (!Array.isArray(toolValue) || toolValue.length === 0) {
    return null;
  }

  let hydratedItems = [];
  switch (toolName) {
    case "Line":
      hydratedItems = toolValue.map(hydrateLineItem).filter(Boolean);
      break;
    case "AiSegmentation":
      hydratedItems = toolValue.map(hydrateAiSegmentationItem).filter(Boolean);
      break;
    case "FluidSegmentation":
      hydratedItems = toolValue
        .map(hydrateFluidSegmentationItem)
        .filter(Boolean);
      break;
    case "Lison":
      hydratedItems = toolValue.map(hydrateLisonItem).filter(Boolean);
      break;
    default:
      hydratedItems = toolValue.map(hydrateHandlePairItem).filter(Boolean);
      break;
  }

  return hydratedItems.length ? { data: hydratedItems } : null;
}

function serializeOpTool(toolName, toolState) {
  if (toolName === "Etdrs") {
    const items = toolDataToArray(toolState)
      .map(serializeEtdrsItem)
      .filter(Boolean);
    return items.length ? items : null;
  }

  const items = toolDataToArray(toolState)
    .map(serializeHandlePairItem)
    .filter(Boolean);
  return items.length ? items : null;
}

function hydrateOpTool(toolName, toolValue) {
  if (isLegacyToolValue(toolValue)) {
    return toolValue;
  }
  if (!Array.isArray(toolValue) || toolValue.length === 0) {
    return null;
  }

  if (toolName === "Etdrs") {
    const items = toolValue.map(hydrateEtdrsItem).filter(Boolean);
    if (!items.length) {
      return null;
    }
    const data = {};
    items.forEach((item, index) => {
      data[String(index)] = item;
    });
    return { data };
  }

  const items = toolValue.map(hydrateHandlePairItem).filter(Boolean);
  if (!items.length) {
    return null;
  }
  const data = {};
  items.forEach((item, index) => {
    data[String(index)] = item;
  });
  return { data };
}

function serializeOptFrames(optAnnotations) {
  if (!optAnnotations || typeof optAnnotations !== "object") {
    return {};
  }

  const frames = {};
  Object.keys(optAnnotations).forEach((frameKey) => {
    const normalizedKey = normalizeFrameKey(frameKey);
    const frameData = optAnnotations[frameKey];
    if (!frameData || typeof frameData !== "object") {
      return;
    }

    const compactFrame = {};
    Object.keys(frameData).forEach((toolName) => {
      const serialized = serializeOptTool(toolName, frameData[toolName]);
      if (serialized != null) {
        compactFrame[toolName] = serialized;
      }
    });

    if (Object.keys(compactFrame).length > 0) {
      frames[normalizedKey] = compactFrame;
    }
  });

  return frames;
}

function hydrateOptFrames(optAnnotations) {
  if (!optAnnotations || typeof optAnnotations !== "object") {
    return {};
  }

  const frames = {};
  Object.keys(optAnnotations).forEach((frameKey) => {
    const normalizedKey = normalizeFrameKey(frameKey);
    const frameData = optAnnotations[frameKey];
    if (!frameData || typeof frameData !== "object") {
      return;
    }

    const legacyFrame = {};
    Object.keys(frameData).forEach((toolName) => {
      const hydrated = hydrateOptTool(toolName, frameData[toolName]);
      if (hydrated != null) {
        legacyFrame[toolName] = hydrated;
      }
    });

    if (Object.keys(legacyFrame).length > 0) {
      frames[normalizedKey] = legacyFrame;
    }
  });

  return frames;
}

function serializeOpAnnotations(opAnnotations) {
  if (!opAnnotations || typeof opAnnotations !== "object") {
    return {};
  }

  const compactOp = {};
  Object.keys(opAnnotations).forEach((key) => {
    if (PASS_THROUGH_OP_KEYS.has(key)) {
      compactOp[key] = opAnnotations[key];
      return;
    }
    if (EXCLUDED_OP_TOOLS.has(key)) {
      return;
    }

    const serialized = serializeOpTool(key, opAnnotations[key]);
    if (serialized != null) {
      compactOp[key] = serialized;
    }
  });

  return compactOp;
}

function hydrateOpAnnotations(opAnnotations) {
  if (!opAnnotations || typeof opAnnotations !== "object") {
    return {};
  }

  if (!isCompactAnnotationFormat({ OP: opAnnotations })) {
    return opAnnotations;
  }

  const legacyOp = {};
  Object.keys(opAnnotations).forEach((key) => {
    if (PASS_THROUGH_OP_KEYS.has(key)) {
      legacyOp[key] = opAnnotations[key];
      return;
    }

    const hydrated = hydrateOpTool(key, opAnnotations[key]);
    if (hydrated != null) {
      legacyOp[key] = hydrated;
    }
  });

  return legacyOp;
}

export function serializeAnnotationsForSave(stackEntry) {
  if (!stackEntry || typeof stackEntry !== "object") {
    return {
      annotationFormatVersion: ANNOTATION_FORMAT_VERSION,
      OP: {},
      OPT: {},
    };
  }

  return {
    annotationFormatVersion: ANNOTATION_FORMAT_VERSION,
    meta: stackEntry.meta || {},
    OP: serializeOpAnnotations(stackEntry.OP || {}),
    OPT: serializeOptFrames(stackEntry.OPT || {}),
  };
}

export function hydrateAnnotationsForRestore(payload) {
  if (!payload || typeof payload !== "object") {
    return { OP: {}, OPT: {} };
  }

  if (!isCompactAnnotationFormat(payload)) {
    return {
      OP: payload.OP || {},
      OPT: payload.OPT || {},
      meta: payload.meta,
    };
  }

  return {
    meta: payload.meta,
    OP: hydrateOpAnnotations(payload.OP || {}),
    OPT: hydrateOptFrames(payload.OPT || {}),
  };
}

export function hydrateOpAnnotationsForRestore(opAnnotations) {
  return hydrateOpAnnotations(opAnnotations);
}

export function hydrateOptAnnotationsForRestore(optAnnotations) {
  if (!optAnnotations || typeof optAnnotations !== "object") {
    return {};
  }

  if (isCompactAnnotationFormat({ OPT: optAnnotations })) {
    return hydrateOptFrames(optAnnotations);
  }

  return optAnnotations;
}

export function getEtdrsVolumesFromAnnotation(etdrsAnnotation) {
  if (!etdrsAnnotation) {
    return null;
  }

  if (Array.isArray(etdrsAnnotation)) {
    return etdrsAnnotation[0]?.volumesData || null;
  }

  const firstItem = etdrsAnnotation?.data?.[0] || etdrsAnnotation?.data?.["0"];
  return firstItem?.volumesData || null;
}

export function getEtdrsCenterFromAnnotation(etdrsAnnotation) {
  if (!etdrsAnnotation) {
    return null;
  }

  if (Array.isArray(etdrsAnnotation)) {
    const center = etdrsAnnotation[0]?.center;
    if (!Array.isArray(center) || center.length < 2) {
      return null;
    }
    return { x: center[0], y: center[1] };
  }

  return (
    etdrsAnnotation?.data?.[0]?.handles?.start ||
    etdrsAnnotation?.data?.["0"]?.handles?.start ||
    null
  );
}

export function hasCompactOrLegacyEtdrsVolumesData(etdrsAnnotation) {
  const volumesData = getEtdrsVolumesFromAnnotation(etdrsAnnotation);
  if (!volumesData || typeof volumesData !== "object") {
    return false;
  }
  return Boolean(
    volumesData.regions ||
      (volumesData.totals && Object.keys(volumesData.totals).length > 0),
  );
}

export function getEtdrsFirstEntry(etdrsAnnotation) {
  if (!etdrsAnnotation) {
    return null;
  }
  if (Array.isArray(etdrsAnnotation)) {
    return etdrsAnnotation[0] || null;
  }
  return etdrsAnnotation?.data?.[0] || etdrsAnnotation?.data?.["0"] || null;
}

const REDUX_PASS_THROUGH_OP_KEYS = new Set(["lisonsAreasData"]);

/** Normalize OP tool state so Redux/UI consumers always see array-backed `data`. */
export function normalizeOpAnnotationsForRedux(opAnnotations) {
  if (!opAnnotations || typeof opAnnotations !== "object") {
    return {};
  }

  const normalized = {};
  Object.entries(opAnnotations).forEach(([key, value]) => {
    if (REDUX_PASS_THROUGH_OP_KEYS.has(key)) {
      normalized[key] = value;
      return;
    }
    if (!value || typeof value !== "object") {
      return;
    }

    const items = getToolStateDataItems(value);
    if (!items.length && value.data == null) {
      return;
    }

    normalized[key] = {
      ...value,
      data: items,
    };
  });

  return normalized;
}

/** Hydrate compact v2 series annotations once for all viewer consumers. */
export function resolveViewAnnotations(annotations) {
  if (!annotations || typeof annotations !== "object") {
    return { OP: {}, OPT: {} };
  }

  const hydrated = hydrateAnnotationsForRestore(annotations);
  return {
    OP: hydrated.OP || {},
    OPT: hydrated.OPT || {},
    meta: hydrated.meta || annotations.meta,
  };
}
